import { ArrowLeft, ArrowRight, Lock } from 'lucide-react';
import { CoinIcon } from '@/components/coins';
import { usePaywall } from '@/hooks/usePaywall';
import type { QuizView } from '@/types/api';

/**
 * QuizIntro — optional pre-quiz splash (quiz spec §4.1). Shown on first
 * entry to a quiz; the parent page skips it on "Try again". Keep it
 * short — copy is the pressure release, not a gate.
 */
export interface QuizIntroProps {
  quiz: QuizView;
  onStart: () => void;
  onBack: () => void;
  /** When true the reader already earned coins from this quiz — hide the reward teaser. */
  alreadyEarnedCoins?: boolean;
}

export function QuizIntro({ quiz, onStart, onBack, alreadyEarnedCoins }: QuizIntroProps) {
  const { isPaywalled, openPaywall } = usePaywall();

  // Free-tier readers can browse the quiz intro (so they see what's on
  // offer) but the Start button intercepts to the paywall instead of
  // kicking off the attempt. Paid users / admins fall through to the
  // normal `onStart` handler.
  const handleStart = () => {
    if (isPaywalled) {
      openPaywall('quiz');
      return;
    }
    onStart();
  };

  return (
    <section className="flex flex-col items-center text-center">
      <button
        type="button"
        onClick={onBack}
        className="self-start inline-flex items-center gap-1.5 text-[12px] font-semibold text-or-slate-500 hover:text-or-slate-700"
      >
        <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
        Back
      </button>

      <div
        className="mt-6 inline-flex items-center gap-1.5"
        style={{
          fontFamily: 'Inter, sans-serif',
          fontSize: '11px',
          fontWeight: 600,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: '#1F6A6A',
        }}
      >
        Ollie Learns quiz
      </div>

      <h1
        style={{
          fontFamily: '"DM Serif Display", serif',
          fontWeight: 400,
          fontSize: '36px',
          lineHeight: 1.1,
          letterSpacing: '-0.01em',
          color: '#1F1B16',
          margin: '12px 0 10px',
          maxWidth: '520px',
        }}
      >
        Ready to test yourself?
      </h1>
      <p
        style={{
          fontFamily: 'Inter, sans-serif',
          fontSize: '15px',
          lineHeight: 1.55,
          color: '#8A7F72',
          maxWidth: '480px',
        }}
      >
        {quiz.questions.length} question{quiz.questions.length === 1 ? '' : 's'}. No timer. Tap any
        answer — we'll tell you why.
      </p>

      {/* Coin reward teaser — hidden when the reader has already earned
          coins from this quiz (retakes don't double-pay). */}
      {!alreadyEarnedCoins && (
        <div
          className="mt-8 flex items-center"
          style={{
            gap: '16px',
            backgroundColor: '#FEF3C7',
            border: '1px solid #FDE68A',
            borderRadius: '16px',
            padding: '16px 20px',
            maxWidth: '440px',
            textAlign: 'left',
          }}
        >
          <div
            style={{
              width: '56px',
              height: '56px',
              borderRadius: '999px',
              backgroundColor: '#FFFFFF',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
            aria-hidden
          >
            <CoinIcon size={40} inert />
          </div>
          <div className="flex flex-col">
            <span
              style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '11px',
                fontWeight: 600,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: '#92400E',
              }}
            >
              Pass the Quiz to Earn
            </span>
            <span
              style={{
                fontFamily: '"DM Serif Display", serif',
                fontSize: '20px',
                color: '#78350F',
                lineHeight: 1.2,
                marginTop: '2px',
              }}
            >
              {quiz.coinReward} Coin{quiz.coinReward === 1 ? '' : 's'}
            </span>
            <span
              style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '12px',
                color: '#9A5A10',
                marginTop: '4px',
              }}
            >
              Get {quiz.passThreshold}/{quiz.questions.length} correct to earn your coins.
            </span>
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={handleStart}
        aria-label={isPaywalled ? 'Start quiz — locked, pick a plan to unlock' : 'Start quiz'}
        className="mt-8 inline-flex items-center gap-2 rounded-full border-none"
        style={{
          backgroundColor: '#2E8A8A',
          color: '#FFFFFF',
          padding: '12px 22px',
          fontFamily: 'Inter, sans-serif',
          fontSize: '14px',
          fontWeight: 600,
          boxShadow: '0 1px 2px rgba(31, 27, 22, 0.04)',
          cursor: 'pointer',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = '#1F6A6A';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = '#2E8A8A';
        }}
      >
        {isPaywalled ? <Lock className="h-4 w-4" aria-hidden /> : null}
        Start quiz
        {!isPaywalled ? <ArrowRight className="h-4 w-4" aria-hidden /> : null}
      </button>
      {isPaywalled ? (
        <p
          className="mt-3 text-[12px]"
          style={{ color: '#8A7F72', fontFamily: 'Inter, sans-serif' }}
        >
          Quizzes are part of Ollie Premium — pick a plan to start playing.
        </p>
      ) : null}
    </section>
  );
}
